import { useEffect } from 'react';
import { comics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageHead from '@/components/feature/PageHead';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9onccaqs38sj6jc7c10';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const stats = [
    { value: '12,000+', label: '精选漫画' },
    { value: '500万+', label: '日活跃读者' },
    { value: '8,000万+', label: '总阅读次数' },
    { value: '99.9%', label: '用户好评率' },
  ];

  const milestones = [
    { year: '2020', event: '甜柚漫画平台正式上线，首批引入3000+部热门漫画' },
    { year: '2021', event: '推出竖屏下拉式阅读功能，移动端阅读体验大幅提升' },
    { year: '2022', event: '日活用户突破100万，成为国内领先的免费漫画平台' },
    { year: '2023', event: '漫画库存突破万部，覆盖玄幻、都市、恋爱等全品类' },
    { year: '2024', event: '日活用户突破500万，累计阅读量突破8000万次' },
  ];

  const hotComics = comics.slice(0, 6);

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '甜柚漫画',
    url: 'https://www.tianyoucomics.com',
    description: '甜柚漫画支持下拉式阅读，无限滚动，手机下滑畅读，免费全集漫画，热门漫画一网打尽。',
    sameAs: [
      'https://weibo.com/tianyoucomics',
      'https://www.tianyoucomics.com',
    ],
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于甜柚漫画',
    description: '甜柚漫画 - 中国领先的免费漫画在线阅读平台，支持下拉式阅读、无限滚动和竖屏阅读体验',
    url: 'https://www.tianyoucomics.com/about',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <PageHead
        title="关于我们 - 甜柚漫画 | 品牌介绍与联系方式"
        description="甜柚漫画品牌介绍，了解甜柚漫画的发展历程、核心优势与联系方式。甜柚漫画支持下拉式阅读，无限滚动，免费全集漫画。"
        keywords="甜柚漫画,关于我们,品牌介绍,联系方式,下拉式阅读,免费漫画平台"
        canonicalPath="/about"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Hero */}
          <section className="relative py-16 md:py-20 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20inviting%20Chinese%20style%20background%20with%20soft%20coral%20and%20cream%20gradients%2C%20abstract%20comic%20panel%20motifs%2C%20gentle%20warm%20lighting%2C%20cozy%20reading%20atmosphere%2C%20minimalist%20design%2C%20artistic%20composition&width=1400&height=450&seq=about-hero&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-center"
              width="1400"
              height="450"
            />
            <div className="absolute inset-0 bg-background-50/60"></div>
            <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-4">
                关于甜柚漫画
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
                甜柚漫画致力于为漫画爱好者提供最优质的阅读体验。我们首创下拉式竖屏阅读模式，让手机看漫画变得前所未有的畅快。无限滚动、免费全集，热门漫画一网打尽。
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-2xl md:text-3xl font-heading text-primary-500 mb-1">{stat.value}</div>
                    <p className="text-xs md:text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">品牌故事</h2>
                <div className="prose prose-sm max-w-none">
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    <strong>甜柚漫画</strong>诞生于2020年，由一群热爱漫画的年轻团队创立。我们的初心很简单：让每个人都能在手机上轻松、免费地享受漫画的乐趣。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    我们深知传统翻页式漫画阅读在手机上体验不佳——频繁点击翻页打断阅读节奏，加载等待消磨耐心。因此，我们独创了<strong>竖屏下拉式阅读</strong>模式，用户只需向下滑动，漫画内容便会无缝衔接、无限滚动，带来如流水般顺畅的沉浸式阅读体验。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    经过数年的发展，甜柚漫画已成为国内领先的免费漫画阅读平台之一。我们始终坚持<strong>免费全集</strong>的理念，让每一位读者都能毫无负担地畅享精彩漫画内容。玄幻、都市、恋爱、科幻、穿越、搞笑……十大品类、万余部漫画，满足你的所有阅读需求。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8 text-center">发展历程</h2>
              <div className="max-w-2xl mx-auto">
                {milestones.map((m, index) => (
                  <div key={m.year} className="flex gap-4 md:gap-6 pb-8 last:pb-0">
                    <div className="flex flex-col items-center">
                      <div className="w-3 h-3 rounded-full bg-accent-500 flex-shrink-0"></div>
                      {index < milestones.length - 1 && (
                        <div className="w-0.5 flex-1 bg-background-300 mt-1"></div>
                      )}
                    </div>
                    <div className="pb-1">
                      <span className="text-xs font-semibold text-accent-500 bg-accent-100 px-2 py-0.5 rounded whitespace-nowrap">
                        {m.year}
                      </span>
                      <p className="text-sm text-foreground-700 mt-1.5 leading-relaxed">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Hot Comics Showcase */}
          <section className="py-10 md:py-14 bg-background-50">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">
                <i className="ri-fire-line text-primary-500 mr-2"></i>
                热门漫画推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-product-shop>
                {hotComics.map((comic) => (
                  <a
                    key={comic.id}
                    href={`/comic/${comic.id}`}
                    className="group block text-center"
                  >
                    <div className="aspect-[5/7] rounded-lg overflow-hidden bg-background-100 mb-2.5">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        width="200"
                        height="280"
                      />
                    </div>
                    <h4 className="text-xs font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {comic.title}
                    </h4>
                    <p className="text-[11px] text-foreground-500 mt-0.5">{comic.author}</p>
                  </a>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="px-4 md:px-6 max-w-7xl mx-auto">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-6 text-center">联系我们</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-8">
                  <div className="text-center p-5 rounded-lg bg-background-50">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-mail-line text-lg text-primary-500"></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                    <p className="text-xs text-foreground-500">contact@tianyoucomics.com</p>
                  </div>
                  <div className="text-center p-5 rounded-lg bg-background-50">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                      <i className="ri-wechat-line text-lg text-accent-500"></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h4>
                    <p className="text-xs text-foreground-500">甜柚漫画</p>
                  </div>
                  <div className="text-center p-5 rounded-lg bg-background-50">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                      <i className="ri-weibo-line text-lg text-secondary-500"></i>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">官方微博</h4>
                    <p className="text-xs text-foreground-500">@甜柚漫画</p>
                  </div>
                </div>

                <form
                    action={FORM_SUBMIT_ADDR}
                    method="POST"
                    className="bg-background-50 rounded-lg p-5 md:p-6"
                    data-readdy-form
                    id="contact-form"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const honeypot = form.querySelector('input[name="website_alt"]') as HTMLInputElement;
                      if (honeypot && honeypot.value.trim() !== '') {
                        const successEl = form.querySelector('.form-success') as HTMLElement;
                        if (successEl) successEl.style.display = 'block';
                        return;
                      }
                      try {
                        const formData = new FormData(form);
                        formData.delete('website_alt');
                        const res = await fetch(FORM_SUBMIT_ADDR, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                        });
                        const text = await res.text();
                        let serverMsg = '';
                        try {
                          const parsed = JSON.parse(text);
                          serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
                        } catch {
                          serverMsg = text;
                        }
                        const errorEl = form.querySelector('.form-error') as HTMLElement;
                        const successEl = form.querySelector('.form-success') as HTMLElement;
                        if (res.ok && serverMsg && !serverMsg.includes('spam')) {
                          form.reset();
                          if (successEl) successEl.style.display = 'block';
                          if (errorEl) errorEl.style.display = 'none';
                        } else {
                          if (errorEl) {
                            errorEl.textContent = serverMsg || '提交失败，请稍后重试';
                            errorEl.style.display = 'block';
                          }
                          if (successEl) successEl.style.display = 'none';
                        }
                      } catch {
                        const errorEl = form.querySelector('.form-error') as HTMLElement;
                        if (errorEl) {
                          errorEl.textContent = '网络错误，请稍后重试';
                          errorEl.style.display = 'block';
                        }
                      }
                    }}
                  >
                    <h3 className="text-lg font-heading text-foreground-900 mb-4">发送消息</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-600 mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-600 mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入邮箱"
                        />
                      </div>
                    </div>
                    <div className="mb-4">
                      <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-600 mb-1.5">主题</label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div className="mb-4">
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-600 mb-1.5">留言</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入留言内容（最多500字）"
                      ></textarea>
                      <p className="text-[11px] text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      className="px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>
                    <p className="form-success mt-3 text-sm text-green-600" style={{ display: 'none' }}>
                      <i className="ri-check-line mr-1"></i>消息已发送，我们会尽快回复！
                    </p>
                    <p className="form-error mt-3 text-sm text-red-500" style={{ display: 'none' }}></p>
                  </form>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}